/**
 * SUNTRONIX ONE — GLOBAL UI TERMINOLOGY STANDARD (permanent platform rule).
 *
 * TENANT-FACING = "OUR", NEVER "MY".
 * Every screen, module, nav item, heading or data area shown to a tenant
 * (customer_* roles) must use "Our …" ownership language. Internal Suntronix
 * staff keep "My …" for their personal workspaces (admin exception).
 *
 * HOW TO COMPLY IN NEW CODE:
 *  - Nav/constants shared with tenants: wrap the tenant export in
 *    tenantifyNav([...]) — any "My …" label is converted automatically.
 *  - Shared pages/components: use own(user, "Devices") instead of a
 *    hard-coded "My Devices" heading.
 * Enforced by src/lib/terminology.test.js — do not remove.
 * This is presentation only: it must never change permissions, tenant
 * isolation, RBAC or data ownership.
 */

export const TENANT_OWNERSHIP = "Our";
export const INTERNAL_OWNERSHIP = "My";

export function isTenantUser(userOrRole) {
  const role = typeof userOrRole === "string" ? userOrRole : userOrRole?.role;
  return typeof role === "string" && role.startsWith("customer");
}

export function ownershipWord(userOrRole) {
  return isTenantUser(userOrRole) ? TENANT_OWNERSHIP : INTERNAL_OWNERSHIP;
}

/** Role-aware ownership label: own(user, "Devices") → "Our Devices" | "My Devices". */
export function own(userOrRole, noun) {
  return `${ownershipWord(userOrRole)} ${noun}`;
}

/** "My devices" / "MY ONE" → "Our devices" / "Our ONE" (leading word only). */
export function tenantifyLabel(value) {
  if (typeof value !== "string") return value;
  return value.replace(/^(\s*)(?:My|MY|my)\b/, `$1${TENANT_OWNERSHIP}`);
}

const LABEL_KEYS = new Set(["label", "section", "title"]);

/** Deep-converts every label/section/title in a tenant nav tree to "Our …".
 *  Non-mutating; icons/functions pass through untouched. */
export function tenantifyNav(node) {
  if (Array.isArray(node)) return node.map(tenantifyNav);
  if (node && typeof node === "object" && node.constructor === Object) {
    const out = {};
    for (const [k, v] of Object.entries(node)) {
      out[k] = LABEL_KEYS.has(k) && typeof v === "string"
        ? tenantifyLabel(v) : tenantifyNav(v);
    }
    return out;
  }
  return node;
}
